import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Heart, Brain, Users } from 'lucide-react';
import CTA from '../../components/CTA';

const CraigavonWeightManagement: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Weight Management Craigavon | Emotional Eating Hypnotherapy Lurgan</title>
        <meta
          name="description"
          content="Weight management hypnotherapy serving Craigavon from Belfast clinic (25 miles). Address emotional eating, stress eating patterns, sustainable behavior change. CNHC registered."
        />
        <meta name="keywords" content="weight loss hypnotherapy Craigavon, emotional eating Lurgan, weight management Portadown, stress eating Craigavon, mindful eating Lurgan" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/craigavon-weight-management/" />
        <meta property="og:title" content="Weight Management Craigavon | Emotional Eating Hypnotherapy Lurgan" />
        <meta property="og:description" content="Weight management hypnotherapy serving Craigavon. Address emotional eating, stress eating patterns, sustainable lifestyle change." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Weight Management Craigavon",
            "description": "CNHC registered weight management hypnotherapy serving Craigavon from Belfast clinic. Specializing in emotional eating, stress eating patterns, and sustainable behavior change without diet culture.",
            "url": "https://www.sharonclare-hypnotherapy.com/craigavon-weight-management/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Weight Management and Emotional Eating Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Craigavon, County Armagh",
              "containedInPlace": {
                "@type": "Country",
                "name": "Northern Ireland"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Weight Management Hypnotherapy Serving Craigavon
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Address emotional eating and stress eating patterns with compassionate, evidence-based support
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">25 miles</span> from Craigavon via M1 • <span className="font-semibold">30-35 min drive</span>
                </p>
              </div>
              <p className="text-gray-600 mt-2">Online sessions also available for Craigavon residents</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Weight Management for Craigavon
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                I serve the Craigavon borough community from my Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast, located approximately 25 miles north via the M1 motorway. The journey takes just 30-35 minutes from Junction 10, providing accessible weight management support at a manageable distance that makes regular in-person sessions entirely practical for residents of Craigavon, Lurgan, and Portadown.
              </p>
              <p>
                Weight struggles in Craigavon often connect to the unique pressures of modern suburban life. As Northern Ireland's largest planned "new town," Craigavon's car-dependent sprawl, daily M1 commutes to Belfast or other employment centers, and the challenge of maintaining work-life balance while juggling dual-income family responsibilities all create fertile ground for stress eating patterns. If you find yourself eating in the car during your commute, snacking at your desk to cope with work pressure, or reaching for convenience foods because you're too exhausted to cook after a long day, you're experiencing emotional eating - and this isn't about willpower.
              </p>
              <p>
                Solution-focused hypnotherapy for weight management works differently from diets. Instead of focusing on restriction, calorie counting, and food rules that eventually fail, we address why you eat when you're not physically hungry. By calming your stress response, building emotional resilience, and changing your relationship with food at a subconscious level, you can achieve sustainable behavior change without the cycle of deprivation and rebound that characterizes most diets. My approach is compassionate, non-judgmental, and focused on long-term lifestyle change rather than unrealistic quick fixes or diet culture promises.
              </p>
              <p>
                Many Craigavon residents work in retail at Rushmere Shopping Centre or other service sector jobs, facing customer-facing stress and irregular schedules that disrupt eating patterns. Others commute daily to Belfast for office work, developing habits like eating breakfast in the car, desk snacking to manage work stress, and evening comfort eating to decompress from the commute. These patterns aren't moral failures - they're understandable responses to the pressures of modern working life. Hypnotherapy helps you develop healthier coping strategies that don't involve food.
              </p>
              <p>
                For Craigavon clients unable to make the 30-minute drive to Belfast regularly, or who simply prefer the convenience of remote sessions, I also offer secure online hypnotherapy via video call. Online sessions provide the same professional, evidence-based treatment with the flexibility to attend from your home in Craigavon, Lurgan, or Portadown. This option has been particularly valuable for clients with caring responsibilities, irregular work hours, or those who want to integrate therapy into their busy schedules without adding travel time. The therapeutic effectiveness is equivalent to in-person sessions while offering maximum convenience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why This Approach Works */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Why Hypnotherapy Works for Weight Management
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-xl p-8">
                <Brain className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Addresses Emotional Eating
                </h3>
                <p className="text-gray-700">
                  When stress, anxiety, or difficult emotions trigger eating, it's your subconscious mind running old patterns. Hypnotherapy accesses that subconscious level, breaking the connection between emotions and eating for comfort. You learn healthier ways to manage stress that don't involve food, creating lasting change rather than temporary willpower.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <Heart className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Calms Stress Response
                </h3>
                <p className="text-gray-700">
                  When your body is stuck in stress mode from commuter pressure, work demands, or family responsibilities, hunger hormones increase, cravings intensify, and your brain becomes hyper-focused on high-calorie foods. By teaching your nervous system to switch off the stress response, hypnotherapy naturally reduces cravings and emotional eating urges.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <Users className="w-12 h-12 text-accent-600 mb-4" />
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Sustainable Behavior Change
                </h3>
                <p className="text-gray-700">
                  Diets work short-term but fail long-term because they rely on willpower and restriction. Hypnotherapy creates lasting change by addressing the root causes of overeating - stress, emotional patterns, and subconscious beliefs about food. The changes feel natural, not forced, making sustainable weight management genuinely achievable.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Craigavon Context */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Understanding Stress Eating in Craigavon
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                As someone who has worked extensively with Northern Ireland communities, I understand the specific stressors that contribute to emotional eating patterns in Craigavon. The daily M1 commute for many residents creates what I call "commuter stress eating" - eating breakfast in the car, keeping snacks in the glove compartment, stopping at drive-throughs because you're too tired to cook after the journey home. These aren't character flaws; they're predictable responses to time pressure and chronic low-level stress.
              </p>
              <p>
                For those working in Craigavon itself - whether in retail at Rushmere, in manufacturing or industrial roles, or in service sector jobs - different but equally challenging patterns emerge. Standing all day leads to fatigue eating. Customer-facing stress triggers comfort food cravings during breaks. Irregular shift patterns disrupt normal meal times, leading to grazing and convenience food dependence. Financial pressures and work insecurity add background stress that manifests as emotional eating.
              </p>
              <p>
                Living in a planned suburban development also contributes to eating patterns. The car-dependent sprawl means less incidental exercise and more sedentary time. The lack of an organic community center can lead to social isolation, with food becoming a primary source of comfort and pleasure. The challenge of maintaining work-life balance in a commuter town means convenience often wins over healthy meal planning, creating a cycle that feels impossible to break.
              </p>
              <p>
                Desk workers who commute to Belfast face their own specific challenges. Sitting at a computer all day, with easy access to office snacks and treats, creates mindless eating patterns. The afternoon energy slump triggers sugar cravings. Work stress and deadlines lead to emotional snacking without awareness. Then the evening commute home adds more stress, and you find yourself eating to decompress rather than because you're genuinely hungry.
              </p>
              <p>
                What all these patterns have in common is that they're driven by stress and emotions, not by physical hunger. Your subconscious mind has learned that food provides quick relief from difficult feelings - boredom, stress, loneliness, frustration, exhaustion. The problem is that emotional eating creates its own cycle: stress leads to eating, which leads to weight gain and guilt, which leads to more stress and shame, which leads to more eating. Breaking this cycle requires addressing the stress at its root, not just trying to "eat less and move more."
              </p>
              <p>
                Hypnotherapy interrupts this cycle by teaching your nervous system to calm down, reducing the baseline stress that drives emotional eating. When you have healthier ways to manage difficult emotions - stress management techniques, better sleep patterns, emotional resilience - food loses its power as an emotional crutch. When your subconscious beliefs about food, eating, and your body shift from shame and restriction to compassion and balance, sustainable behavior change becomes possible.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8">
              What to Expect from Weight Management Hypnotherapy
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Initial Consultation (90 minutes)
                </h3>
                <p className="text-gray-700 mb-4">
                  We'll explore your weight history, eating patterns, stress triggers, commuter habits, and goals. I'll explain how stress and emotions drive eating behaviors, how your commute and work environment contribute to emotional eating, and how hypnotherapy works at a neurological level to create change. You'll also experience your first hypnotherapy session, leaving feeling calm and motivated.
                </p>
                <p className="text-gray-700">
                  There's no judgment here - only understanding and evidence-based support. Many Craigavon clients express relief at finally being understood, rather than being told they just need more willpower or to try another diet. We'll create a realistic plan that fits your actual life - commute, work schedule, family responsibilities and all.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Ongoing Sessions (4-8 weeks)
                </h3>
                <p className="text-gray-700 mb-4">
                  Most clients benefit from 4-8 sessions, typically weekly or fortnightly. Each session builds on the last, gradually reprogramming your subconscious patterns around food, stress, commuter eating, and emotional eating. Between sessions, you'll use audio recordings to reinforce the positive changes - many clients listen during their M1 commute, turning travel time into therapeutic time.
                </p>
                <p className="text-gray-700">
                  You'll notice changes fairly quickly - reduced cravings, better awareness of hunger and fullness, more control around trigger foods, less car eating and desk snacking, improved sleep (which dramatically affects weight), and a calmer relationship with food and your body. The changes accumulate, creating momentum toward your goals.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-heading font-semibold text-gray-900 mb-3">
                  Long-Term Sustainable Change
                </h3>
                <p className="text-gray-700">
                  The goal isn't just weight loss - it's changing your relationship with food permanently. When you address the emotional and stress-related drivers of overeating, when you develop better coping strategies for commuter stress and work pressure, weight loss becomes a natural side effect of feeling better and managing stress more effectively. This approach creates lasting change rather than the temporary results that diets deliver.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Craigavon Area Clients Say About Weight Management
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I was eating constantly in the car during my M1 commute to Belfast - breakfast on the way in, snacks on the way home. Sharon helped me understand I was stress eating, not actually hungry. After 6 sessions, I've lost a stone and a half and I've broken the car eating habit completely. The drive from Lurgan was worth every minute."
                </p>
                <p className="font-semibold text-gray-900">K.M., Lurgan - Commuter Stress Eating</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Working in retail at Rushmere, I was comfort eating on every break to cope with the customer stress. Sharon's approach isn't about dieting - it's about understanding your emotional eating patterns and finding healthier ways to manage stress. I've lost 2 stone over 7 months and it feels sustainable this time, not like the yo-yo dieting I'd done for years."
                </p>
                <p className="font-semibold text-gray-900">A.T., Portadown - Emotional Eating</p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 italic mb-4">
                  "The work-life balance pressure in Craigavon is real - commuting, juggling kids and work, feeling like there's never enough time. I was eating convenience food and comfort eating constantly. Online sessions from home were perfect for my schedule. Sharon helped me develop better stress management and I've lost nearly 3 stone. It doesn't feel like deprivation - it feels like freedom."
                </p>
                <p className="font-semibold text-gray-900">L.D., Craigavon - Work-Life Balance Stress</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Craigavon Weight Management
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with commuter stress eating?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. Commuter stress eating - eating in the car, grabbing drive-through food, snacking during the journey - is a specific pattern I work with frequently. The M1 commute from Craigavon to Belfast creates chronic low-level stress that triggers eating as a coping mechanism. Hypnotherapy addresses this by teaching your nervous system to stay calm during the commute, breaking the automatic connection between driving stress and eating, and helping you develop better ways to manage the transition between work and home. Many clients find that turning their commute into therapeutic time by listening to hypnotherapy audios creates a positive ritual that replaces the old eating pattern.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Is this different from a diet plan?
                </h3>
                <p className="text-gray-700">
                  Completely different. Diets focus on what you eat - restriction, calorie counting, food rules. They work short-term but fail long-term because they rely on willpower, which is a limited resource that depletes under stress (like commuting, work pressure, family demands). Hypnotherapy addresses why you eat when you're not physically hungry. By resolving the emotional and stress-related drivers of overeating, you naturally make better food choices without feeling deprived or restricted. The changes happen at a subconscious level, so they feel natural rather than forced. This creates sustainable, long-term behavior change without the cycle of deprivation and rebound that characterizes dieting.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How many sessions will I need?
                </h3>
                <p className="text-gray-700">
                  Most clients see significant improvements within 4-8 sessions, typically scheduled weekly or fortnightly. The exact number depends on your individual situation - how long you've struggled with weight, how much stress you're dealing with from commuting and work, and how deeply entrenched your emotional eating patterns are. We'll assess your progress as we go and adjust accordingly. I offer a discounted 4-session package which many Craigavon clients find provides excellent value, especially when factoring in the manageable 30-minute journey from Craigavon. The M1 access makes regular weekly appointments entirely practical.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with desk job snacking?
                </h3>
                <p className="text-gray-700">
                  Yes, desk job snacking is a classic emotional eating pattern that hypnotherapy addresses very effectively. When you're sitting at a computer all day, often stressed by deadlines and work demands, snacking becomes an automatic stress relief mechanism and a way to break the monotony. The problem is that you're not eating because you're hungry - you're eating to manage stress, boredom, or frustration. Hypnotherapy helps by reducing your baseline stress levels, increasing your awareness of true hunger versus emotional hunger, breaking the automatic hand-to-mouth pattern, and teaching you healthier ways to take mental breaks. Many commuting clients find that addressing both their car eating and desk snacking creates significant progress.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can online sessions really work as well as in-person?
                </h3>
                <p className="text-gray-700">
                  Yes, absolutely. Online hypnotherapy sessions via video call are just as effective as face-to-face sessions. The hypnotherapy process works equally well through a screen, and many clients actually find online sessions more convenient and comfortable - you're in your own safe space, there's no 30-minute M1 commute to factor in, and you can schedule sessions around work and family commitments more easily. For Craigavon clients juggling busy lives, irregular work schedules, or caring responsibilities, online sessions often provide the consistency needed for successful weight management without the pressure of travel. You still get the same professional, evidence-based treatment.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Will you give me a meal plan or diet to follow?
                </h3>
                <p className="text-gray-700">
                  No - I don't provide meal plans or dietary advice. My focus is entirely on the psychological and emotional aspects of eating. You already know what healthy eating looks like - the problem isn't lack of information, it's that stress, emotions, and subconscious patterns override that knowledge. Hypnotherapy works on those underlying drivers. When your stress response is calm, when you have healthier coping strategies than food, when your subconscious beliefs about eating shift, you naturally make better food choices without needing a rigid meal plan to follow. This creates much more sustainable change than external food rules that eventually feel like restriction and trigger rebellion. The goal is internal change, not external control.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA />
    </main>
  );
};

export default CraigavonWeightManagement;
